"use client";

import React, { useEffect, useState, useCallback } from "react";
import { Button } from "../../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Settings, Play, FileText, Download, AlertTriangle, Search, Upload, ArrowLeft, Loader2, Database, Table as TableIcon, Plus, X, MessageSquare } from "lucide-react";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { ReviewComment } from "../../shared/ReviewComment";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";
import { SHAREPOINT_BASE_URL, SHAREPOINT_DOC_LIBRARY_URL } from "@/constants/sharepoint-config";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface ActuaryTestingProps { onBack?: () => void; }
interface ClientFile { name: string; reference?: string }
interface MappingRow { id: string; ctc?: string; actuary?: string }

interface DocumentItem {
  id: string;
  type: 'textbox' | 'linked' | 'uploaded';
  name: string;
  reference?: string;
  url?: string;
  content?: string;
}

interface QuestionResponse {
  selectedOption?: string;
  textResponse?: {
    html: string;
    plainText: string;
  } | null;
  documents?: DocumentItem[] | string[];
  table?: any[];
}

export default function ActuaryTesting({ onBack }: ActuaryTestingProps) {
  const { isArchivalMode } = useArchivalMode();
  const [companyName, setCompanyName] = useState<string>("");
  const [questionResponses, setQuestionResponses] = useState<Record<string, QuestionResponse>>({});
  const [inferenceQuestion, setInferenceQuestion] = useState<QuestionResponse>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  // SharePoint search and file management
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  
  // File selections
  const [selectedCTCFile, setSelectedCTCFile] = useState<string>("");
  const [selectedActuaryFile, setSelectedActuaryFile] = useState<string>("");
  const [ctcColumns, setCtcColumns] = useState<string[]>([]);
  const [actuaryColumns, setActuaryColumns] = useState<string[]>([]);
  const [mappings, setMappings] = useState<MappingRow[]>([{ id: `map_${Date.now()}` }]);
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [excelUrl, setExcelUrl] = useState<string>("");
  const [jsonUrl, setJsonUrl] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Load table data
  const [tableData, setTableData] = useState<{
    Actuary_rows: number;
    CTC_Report_rows: number;
    User_Rows: Array<[number, string]>;
  } | null>(null);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [tableFileNotFound, setTableFileNotFound] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [userRows, setUserRows] = useState<Array<{ amount: number; reason: string }>>([]);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `ACTUARY_TESTING_${normalizedSection}`;
  };

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_EBE");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("");
      }
    };
    loadCompanyName();
    loadFileUsageReference();
  }, []);

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Employee Benefits Expense", "Actuary Testing");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Load responses from SharePoint
  useEffect(() => {
    const loadResponses = async () => {
      if (!companyName) {
        console.log('[ActuaryTesting] Waiting for company name...');
        return;
      }
      
      console.log('[ActuaryTesting] Loading documentation responses for company:', companyName);
      
      try {
        if (window.payrollAPI?.loadResponses) {
          const result = await window.payrollAPI.loadResponses({
            companyName,
            responseType: "substantiveProcedures"
          });
          
          console.log('[ActuaryTesting] SharePoint load result:', {
            success: result?.success,
            hasData: !!result?.data,
            dataKeys: result?.data ? Object.keys(result.data) : []
          });
          
          if (result.success && result.data) {
            const loadedResponses: Record<string, QuestionResponse> = {};
            
            for (const [questionId, responseData] of Object.entries(result.data)) {
              const response = responseData as any;
              
              // Load textResponse
              let textResponse = null;
              if (response.textResponse) {
                textResponse = {
                  html: response.textResponse.html || response.textResponse.plainText || '',
                  plainText: response.textResponse.plainText || (response.textResponse.html ? response.textResponse.html.replace(/<[^>]*>/g, '') : '')
                };
                
                // Save to localStorage so DocumentManager can pick it up
                if (textResponse.html) {
                  localStorage.setItem(`textbox_${questionId}`, textResponse.html);
                  console.log(`[ActuaryTesting] Saved textResponse to localStorage for ${questionId}, length: ${textResponse.html.length}`);
                }
              }
              
              // Load documents - convert string array to DocumentItem format if needed
              let documents: any[] = [];
              if (response.documents && Array.isArray(response.documents)) {
                documents = response.documents.map((doc: any) => {
                  if (typeof doc === 'string') {
                    return {
                      id: `doc_${doc}`,
                      type: 'linked' as const,
                      name: doc
                    };
                  }
                  return {
                    id: doc.id || `doc_${doc.name || doc}`,
                    type: doc.type || 'linked',
                    name: doc.name || doc,
                    reference: doc.reference,
                    url: doc.url
                  };
                });
              }
              
              loadedResponses[questionId] = {
                selectedOption: response.selectedOption || "",
                textResponse: textResponse,
                documents: documents,
                table: response.table || []
              };
              
              console.log(`[ActuaryTesting] Loaded response for ${questionId}:`, {
                hasTextResponse: !!textResponse,
                textResponseLength: textResponse?.html?.length || 0,
                documentCount: documents.length
              });
            }
            
            console.log('[ActuaryTesting] Loaded documentation responses:', {
              responseCount: Object.keys(loadedResponses).length,
              questionIds: Object.keys(loadedResponses),
              actuary1TextResponse: !!loadedResponses['Actuary-1']?.textResponse,
              actuary2TextResponse: !!loadedResponses['Actuary-2']?.textResponse
            });
            
            setQuestionResponses(loadedResponses);
            
            // Load inference question
            const inferenceId = "ACTUARY-INFERENCE";
            const inferenceData = result.data[inferenceId];
            if (inferenceData) {
              const inferenceResponse = inferenceData as any;
              let inferenceTextResponse = null;
              if (inferenceResponse.textResponse) {
                inferenceTextResponse = {
                  html: inferenceResponse.textResponse.html || inferenceResponse.textResponse.plainText || '',
                  plainText: inferenceResponse.textResponse.plainText || (inferenceResponse.textResponse.html ? inferenceResponse.textResponse.html.replace(/<[^>]*>/g, '') : '')
                };
                if (inferenceTextResponse.html) {
                  localStorage.setItem(`textbox_${inferenceId}`, inferenceTextResponse.html);
                }
              }
              const inferenceDocuments = (inferenceResponse.documents || []).map((doc: any) => {
                if (typeof doc === 'string') {
                  return { id: `doc_${doc}`, type: 'linked' as const, name: doc };
                }
                return {
                  id: doc.id || `doc_${doc.name || doc}`,
                  type: doc.type || 'linked',
                  name: doc.name || doc,
                  reference: doc.reference,
                  url: doc.url
                };
              });
              setInferenceQuestion({
                selectedOption: "",
                textResponse: inferenceTextResponse,
                documents: inferenceDocuments,
                table: []
              });
            }
          } else {
            console.log('[ActuaryTesting] No documentation responses found in SharePoint (this is normal for first-time use)');
          }
        } else {
          console.warn('[ActuaryTesting] window.payrollAPI.loadResponses is not available');
        }
      } catch (error) {
        console.error('[ActuaryTesting] Failed to load responses from SharePoint:', error);
      }
    };
    
    loadResponses();
  }, [companyName]);

  // Save responses to SharePoint
  const saveResponses = useCallback(async () => {
    if (!companyName) {
      console.warn('[ActuaryTesting] Cannot save: company name not available');
      return;
    }
    
    console.log('[ActuaryTesting] Saving documentation responses to SharePoint...');
    setSaveStatus('saving');
    setSaveError(null);
    
    try {
      const areaPrefix = "EBE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Trigger auto-save for DocumentManager textboxes before saving
      try {
        const autoSaveActuary1 = (window as any)['autoSaveTextbox_Actuary-1'];
        if (autoSaveActuary1 && typeof autoSaveActuary1 === 'function') {
          autoSaveActuary1();
        }
        const autoSaveActuary2 = (window as any)['autoSaveTextbox_Actuary-2'];
        if (autoSaveActuary2 && typeof autoSaveActuary2 === 'function') {
          autoSaveActuary2();
        }
        await new Promise(resolve => setTimeout(resolve, 1500));
      } catch (error) {
        console.warn('[ActuaryTesting] Failed to trigger auto-save for DocumentManager:', error);
      }
      
      // Collect responses for Actuary-1 and Actuary-2
      ['Actuary-1', 'Actuary-2'].forEach(questionId => {
        const currentResponse = questionResponses[questionId] || {};
        
        // Check localStorage for textbox content (highest priority)
        const textboxContent = localStorage.getItem(`textbox_${questionId}`);
        const hasTextboxContent = textboxContent && textboxContent.trim().length > 0;
        const hasTextResponse = currentResponse.textResponse && (
          (currentResponse.textResponse.html && currentResponse.textResponse.html.trim().length > 0) ||
          (currentResponse.textResponse.plainText && currentResponse.textResponse.plainText.trim().length > 0)
        );
        
        // Build documents array (excluding textbox type)
        const fileDocuments = currentResponse.documents 
          ? currentResponse.documents.filter((doc: any) => typeof doc === 'string' || doc.type !== 'textbox').map((doc: any) => {
              if (typeof doc === 'string') {
                return doc;
              }
              return doc.name || doc;
            })
          : [];
        
        // Build textResponse - localStorage takes priority
        let textResponseData = null;
        if (hasTextboxContent) {
          textResponseData = {
            html: textboxContent,
            plainText: textboxContent.replace(/<[^>]*>/g, '')
          };
        } else if (hasTextResponse) {
          textResponseData = currentResponse.textResponse;
        }
        
        existingData.substantiveProcedures[questionId] = {
          selectedOption: currentResponse.selectedOption || "",
          textResponse: textResponseData,
          documents: fileDocuments,
          table: currentResponse.table || [],
          lastUpdated: new Date().toISOString(),
          companyName,
        };
      });

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "ACTUARY-INFERENCE";
      const sectionToFind = `Execution_EBE_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      // Get FY_YEAR from database dynamically
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('[ActuaryTesting] Failed to save responses to SharePoint:', error);
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : 'Failed to save responses');
    }
  }, [companyName, questionResponses, inferenceQuestion]);
  
  const handleDocumentsChange = (questionId: string, documents: any[]) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        documents: documents.map(doc => doc.name || doc)
      }
    }));
  };
  
  const handleTextResponseChange = (questionId: string, textResponse: any) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textResponse
      }
    }));
  };

  // Auto-trigger search for files and load table data when component mounts (when user clicks on Actuary Testing panel)
  useEffect(() => {
    searchForFiles();
    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              // Get FY_YEAR from database dynamically
              const fyYear = getFY_YEAR();
              if (!fyYear) {
                throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
              }

              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: fyYear,
                sectionList: ["Execution_EBE"],
              });

              if (result.success) {
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadColumnsForFile = async (fileName: string, target: "ctc" | "actuary") => {
    if (!window.payroll?.loadExcelColumns || !fileName) return;
    const fileOnly = fileName.includes(" (") ? fileName.split(" (")[0] : fileName;
    const res = await window.payroll.loadExcelColumns(fileOnly);
    if (res.ok && Array.isArray(res.columns)) {
      target === "ctc" ? setCtcColumns(res.columns as string[]) : setActuaryColumns(res.columns as string[]);
      // Save file name to file usage reference
      const key = target === "ctc" ? "CTC File" : "Actuary File";
      await updateProcedureFileUsageForWorkspace("Employee Benefits Expense", "Actuary Testing", [{ [key]: fileOnly }]);
      await loadFileUsageReference();
    }
  };

  const addRow = () => setMappings(prev => [...prev, { id: `map_${Date.now()}` }]);
  const removeRow = (id: string) => setMappings(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);
  const updateRow = (id: string, side: "ctc" | "actuary", value: string) => {
    setMappings(prev => prev.map(r => r.id === id ? { ...r, [side]: value } : r));
  };

  const isValid = () => {
    if (!selectedCTCFile || !selectedActuaryFile) return false;
    if (mappings.length === 0) return false;
    return mappings.every(r => r.ctc && r.actuary);
  };

  const extractJSON = (text: string) => {
    if (!text) return null;
    try { return JSON.parse(text.trim()); } 
    catch {
      for (const line of text.split("\n")) {
        const trimmed = line.trim();
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
          try { return JSON.parse(trimmed); } catch {}
        }
      }
    }
    return null;
  };

  const runActuaryTesting = async () => {
    if (!isValid()) return;
    
    // Save file names and column mappings to file usage reference
    const entries: FileUsageEntry[] = [];
    if (selectedCTCFile) {
      const fileOnly = selectedCTCFile.includes(" (") ? selectedCTCFile.split(" (")[0] : selectedCTCFile;
      entries.push({ "CTC File": fileOnly });
    }
    if (selectedActuaryFile) {
      const fileOnly = selectedActuaryFile.includes(" (") ? selectedActuaryFile.split(" (")[0] : selectedActuaryFile;
      entries.push({ "Actuary File": fileOnly });
    }
    // Save Mapping Table
    const mappingTable = mappings
      .filter(m => m.ctc && m.actuary)
      .map(m => ({ CTC: m.ctc, Actuary: m.actuary }));
    if (mappingTable.length > 0) {
      entries.push({ "Mapping Table": JSON.stringify(mappingTable) });
    }
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Employee Benefits Expense", "Actuary Testing", entries);
      await loadFileUsageReference();
    }
  
    setProcessingStatus("running");
    setErrorMessage("");
    setExcelUrl("");
    setJsonUrl("");
  
    try {
      // Strip whitespace and newlines from column names to match Excel processing
      const stripColumnName = (name: string) => String(name).trim().replace(/\n/g, '');
      
      const column_map = mappings
        .filter(m => m.ctc && m.actuary)
        .map(m => ({ 
          CTC: stripColumnName(m.ctc!), 
          Actuary: stripColumnName(m.actuary!)
        }));

      const sanitize = (name: string) => name.includes(" (") ? name.split(" (")[0] : name;
      const ctcFileOnly = sanitize(selectedCTCFile);
      const actuaryFileOnly = sanitize(selectedActuaryFile);
      
      // Ensure we're using the selected Actuary file
      if (!actuaryFileOnly) {
        setProcessingStatus("error");
        setErrorMessage("Please select an Actuary file.");
        return;
      }
  
      if (!window.payroll?.run) {
        setProcessingStatus("error");
        setErrorMessage("Runner API unavailable.");
        return;
      }
  
      const res = await window.payroll.run("execute_actuary_testing", {
        inputFiles: [ctcFileOnly, actuaryFileOnly],
        options: {
          ctc_file: ctcFileOnly,
          actuary_file: actuaryFileOnly,
          input_mapping: { column_map },
        },
      });
  
      console.log("Runner start result:", res);
  
      if (!res.ok) {
        setProcessingStatus("error");
        setErrorMessage(res.error || "Failed to start runner.");
        return;
      }
  
      setProcessingStatus("completed");
      if (res.stdout) {
        try {
          const stdoutStr = String(res.stdout);
          // Check for JSON error messages in stdout
          const lines = stdoutStr.split('\n');
          for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith('{') && (trimmed.includes('"success": false') || trimmed.includes('"error"'))) {
              try {
                const parsed = JSON.parse(trimmed);
                if (parsed.success === false && parsed.error) {
                  setErrorMessage(parsed.error);
                  setProcessingStatus("error");
                  return;
                }
              } catch {}
            }
          }
          // Extract URLs from stdout
          const jsonLine = lines.find((l: string) => l.trim().startsWith('{') && l.includes('success'));
          if (jsonLine) {
            const parsed = JSON.parse(jsonLine);
            if (parsed.excel_url) setExcelUrl(parsed.excel_url);
            if (parsed.json_url) setJsonUrl(parsed.json_url);
          }
        } catch {}
      }
  
    } catch (err) {
      console.error("Exception in runActuaryTesting:", err);
      setProcessingStatus("error");
      setErrorMessage(`Unexpected error: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const loadTableData = async () => {
    setIsLoadingTable(true);
    setTableFileNotFound(false);
    try {
      // Call IPC handler to download JSON from SharePoint
      if (!window.sharePointAPI?.downloadActuaryTestingJson) {
        throw new Error("SharePoint API method 'downloadActuaryTestingJson' is not available. Please restart the application.");
      }
      const result = await window.sharePointAPI.downloadActuaryTestingJson();
      
      if (result?.success && result?.data) {
        const data = result.data;
        // Check if the data is the default empty data (file not found)
        // This happens when the backend returns all zeros/empty values for a 404 response
        const isDefaultEmptyData = 
          data.Actuary_rows === 0 &&
          data.CTC_Report_rows === 0 &&
          (!Array.isArray(data.User_Rows) || data.User_Rows.length === 0);
        
        if (isDefaultEmptyData) {
          // File not found - show message instead of table
          setTableFileNotFound(true);
          setTableData(null);
          setUserRows([]);
        } else {
          // Valid data exists - show table
          setTableFileNotFound(false);
          setTableData(data);
          // Initialize user rows from loaded data or empty array
          if (Array.isArray(data.User_Rows) && data.User_Rows.length > 0) {
            // Check if it's an array of arrays: [[amount, reason], ...]
            if (Array.isArray(data.User_Rows[0])) {
              // Convert [[amount, reason], ...] to [{ amount, reason }, ...]
              const parsedUserRows = data.User_Rows.map((item: any[]) => {
                return {
                  amount: typeof item[0] === 'number' ? item[0] : 0,
                  reason: typeof item[1] === 'string' ? item[1] : ""
                };
              });
              setUserRows(parsedUserRows);
            } else if (data.User_Rows[0]?.amount !== undefined) {
              // If it's already an array of objects with amount and reason, use it directly
              setUserRows(data.User_Rows);
            } else {
              // Otherwise initialize as empty
              setUserRows([]);
            }
          } else {
            setUserRows([]);
          }
        }
      } else {
        // result.success is false - file not found or other error
        setTableFileNotFound(true);
        setTableData(null);
        setUserRows([]);
      }
    } catch (error) {
      console.error("Error loading table data:", error);
      // On error, treat as file not found and show message
      setTableFileNotFound(true);
      setTableData(null);
      setUserRows([]);
    } finally {
      setIsLoadingTable(false);
    }
  };

  const addUserRow = () => {
    setUserRows([...userRows, { amount: 0, reason: "" }]);
  };

  const removeUserRow = (index: number) => {
    setUserRows(userRows.filter((_, i) => i !== index));
  };

  const updateUserRow = (index: number, field: "amount" | "reason", value: number | string) => {
    const updated = [...userRows];
    updated[index] = { ...updated[index], [field]: value };
    setUserRows(updated);
  };

  const reuploadData = async () => {
    if (!tableData) {
      alert("Please load table data first");
      return;
    }

    setIsUploading(true);
    try {
      // Calculate sum of user rows amounts
      const userRowsSum = userRows.reduce((sum, row) => sum + (row.amount || 0), 0);

      // Format user rows as array of arrays: [[amount, reason], [amount, reason]]
      const userRowsFormatted = userRows.map(row => [row.amount || 0, row.reason || ""]);

      // Prepare the JSON data with current UI state
      // Difference = Actuary_rows - CTC_Report_rows
      // Net Difference = Difference - sum of user rows
      const calculatedDifference = tableData.Actuary_rows - tableData.CTC_Report_rows;
      const jsonData = {
        Actuary_rows: tableData.Actuary_rows,
        CTC_Report_rows: tableData.CTC_Report_rows,
        User_Rows: userRowsFormatted,
        Difference: calculatedDifference,
        Net_Difference: calculatedDifference - userRowsSum
      };

      // Convert to JSON string
      const jsonString = JSON.stringify(jsonData, null, 4);
      
      // Convert to base64 for upload (proper UTF-8 encoding)
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      // Upload using SharePoint API
      if (window.sharePointAPI?.uploadFile) {
        // Get FY_YEAR from database dynamically
        const fyYear = getFY_YEAR();
        if (!fyYear) {
          throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
        }

        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: "Execution_EBE_ActuaryTesting.json",
          referenceValue: "",
          folderName: "juggernaut",
          fyYear: fyYear,
          sectionList: ["Execution_EBE_ActuaryTesting"]
        });

        if (result.success) {
          alert("Successfully uploaded actuary testing data to SharePoint!");
          // Reload table data after successful upload
          await loadTableData();
        } else {
          throw new Error(result.error || "Upload failed");
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Error uploading data:", error);
      alert("Failed to upload data: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Actuary Testing</h2>
          <p className="text-gray-400">CTC vs Actuary reconciliation and mapping</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      {/* Step 1: Search for Existing Files */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
            {fileSearchStatus === "idle" && (
              <span className="text-gray-400">Searching for files...</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">CTC Report</Label>
                  <Select value={selectedCTCFile} onValueChange={async (v) => { setSelectedCTCFile(v); await loadColumnsForFile(v, "ctc"); }}>
                    <SelectTrigger><SelectValue placeholder="Select CTC file..." /></SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                      {foundFiles.map((f, i) => <SelectItem key={i} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["CTC File"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["CTC File"])?.["CTC File"]}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Actuary File</Label>
                  <Select value={selectedActuaryFile} onValueChange={async (v) => { setSelectedActuaryFile(v); await loadColumnsForFile(v, "actuary"); }}>
                    <SelectTrigger><SelectValue placeholder="Select Actuary file..." /></SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                      {foundFiles.map((f, i) => <SelectItem key={i} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Actuary File"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["Actuary File"])?.["Actuary File"]}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={isArchivalMode || uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Mapping Table */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Mapping Table</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Mapping Table"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded border border-white/10 bg-white/5">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-white">CTC</TableHead>
                  <TableHead className="text-white">Actuary</TableHead>
                  <TableHead className="w-28 text-white">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <TableRow>
                      <TableCell>
                        <Select value={row.ctc || ""} onValueChange={v => updateRow(row.id, "ctc", v)}>
                          <SelectTrigger><SelectValue placeholder="Select CTC column..." /></SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">{ctcColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        {index === 0 && fileUsageReference.find(e => e["Mapping Table"]) && (() => {
                          try {
                            const mappingTable = JSON.parse(fileUsageReference.find(e => e["Mapping Table"])?.["Mapping Table"] || "[]");
                            const lastUsed = mappingTable[0]?.CTC;
                            return lastUsed ? (
                              <p className="text-xs text-white/60 mt-1">
                                Last used: {lastUsed}
                              </p>
                            ) : null;
                          } catch {
                            return null;
                          }
                        })()}
                      </TableCell>
                      <TableCell>
                        <Select value={row.actuary || ""} onValueChange={v => updateRow(row.id, "actuary", v)}>
                          <SelectTrigger><SelectValue placeholder="Select Actuary column..." /></SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">{actuaryColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        {index === 0 && fileUsageReference.find(e => e["Mapping Table"]) && (() => {
                          try {
                            const mappingTable = JSON.parse(fileUsageReference.find(e => e["Mapping Table"])?.["Mapping Table"] || "[]");
                            const lastUsed = mappingTable[0]?.Actuary;
                            return lastUsed ? (
                              <p className="text-xs text-white/60 mt-1">
                                Last used: {lastUsed}
                              </p>
                            ) : null;
                          } catch {
                            return null;
                          }
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="border-white/10" onClick={addRow} disabled={isArchivalMode}>Add</Button>
                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeRow(row.id)} disabled={mappings.length === 1}>Remove</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {index === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="py-2">
                          <div className="flex items-center gap-2 text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded px-3 py-2">
                            <AlertTriangle className="h-3 w-3" />
                            <span>First row should be the unique Identifier column of both files</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
          {!isValid() && (
            <div className="flex items-center gap-2 text-xs text-red-300">
              <AlertTriangle className="h-4 w-4" /> Please select both files and map at least one pair of columns.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Execute */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white"><Play className="h-5 w-5" /> Run Actuary Testing</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Run Actuary Testing"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-400">Execute CTC vs Actuary reconciliation</p></div>
            <Button onClick={runActuaryTesting} disabled={isArchivalMode || !isValid() || processingStatus === "running"} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50">
              {processingStatus === "running" ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />Processing...
                </>
              ) : (<><Play className="mr-2 h-4 w-4" />Run Actuary Testing</>)}
            </Button>
          </div>

          {processingStatus === "error" && (
            <div className="rounded border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-200">
              <div className="font-medium mb-1">Error</div>
              <pre className="whitespace-pre-wrap text-xs">{errorMessage || "An error occurred. Please try again."}</pre>
            </div>
          )}

          {processingStatus === "completed" && (
            <div className="rounded border border-green-500/20 bg-green-500/10 p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-green-500" />
                <div>
                  <h4 className="font-medium text-green-300">Actuary Testing Completed Successfully!</h4>
                  <p className="text-sm text-green-200">Files uploaded to SharePoint</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" className="border-green-500/30 text-green-300 hover:bg-green-500/10" onClick={() => excelUrl && window.open(excelUrl, "_blank")} disabled={!excelUrl}>
                  <Download className="mr-2 h-4 w-4" /> Open Excel Output
                </Button>
                <Button variant="outline" className="border-green-500/30 text-green-300 hover:bg-green-500/10" onClick={() => jsonUrl && window.open(jsonUrl, "_blank")} disabled={!jsonUrl}>
                  <FileText className="mr-2 h-4 w-4" /> Open JSON Summary
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Load Table */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <TableIcon className="h-5 w-5" />
              Actuary Testing Summary
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Actuary Testing Summary"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingTable && (
            <div className="flex items-center gap-2 text-blue-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading table data...</span>
            </div>
          )}

          {tableFileNotFound && !isLoadingTable && (
            <div className="flex items-center justify-center p-8 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <p className="text-yellow-400 text-center">
                Actuary Testing has not been run for a summary display.
              </p>
            </div>
          )}

          {tableData && !tableFileNotFound && (
            <div className="space-y-6">
              {/* Display Table Data */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-white/10">
                  <thead>
                    <tr className="bg-black/60">
                      <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                      <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">Number of Employees</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white">As per Data sent to Actuary</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white">{tableData.Actuary_rows}</td>
                    </tr>
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white">As per CTC Report</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white">{tableData.CTC_Report_rows}</td>
                    </tr>
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white font-semibold">Difference</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                        {tableData.Actuary_rows - tableData.CTC_Report_rows}
                      </td>
                    </tr>
                    {userRows.map((row, index) => (
                      <tr key={`user-row-${index}`} className="hover:bg-white/5">
                        <td className="border border-white/10 px-4 py-2 text-white">{row.reason || `User Row ${index + 1}`}</td>
                        <td className="border border-white/10 px-4 py-2 text-right text-white">{row.amount || 0}</td>
                      </tr>
                    ))}
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                        {(tableData.Actuary_rows - tableData.CTC_Report_rows) - userRows.reduce((sum, row) => sum + (row.amount || 0), 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* User Rows */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-white text-lg font-semibold">User Rows</Label>
                  <div className="flex gap-2">
                    <Button
                      onClick={addUserRow}
                      size="sm"
                      variant="outline"
                      className="border-white/10 text-white hover:bg-white/10"
                      disabled={isArchivalMode}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Row
                    </Button>
                    <Button
                      onClick={reuploadData}
                      disabled={isArchivalMode || isUploading || !tableData}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Reupload
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {userRows.length === 0 ? (
                  <p className="text-sm text-gray-400">No user rows added. Click "Add Row" to add one.</p>
                ) : (
                  <div className="space-y-3">
                    {userRows.map((row, index) => (
                      <div key={index} className="flex gap-3 items-start p-3 border border-white/10 rounded-lg bg-black/20">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-white text-sm">Reason</Label>
                            <Input
                              type="text"
                              value={row.reason}
                              onChange={(e) => updateUserRow(index, "reason", e.target.value)}
                              className="border-white/10 bg-black/40 text-white"
                              placeholder="Enter reason"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-white text-sm">Number of Employees</Label>
                            <Input
                              type="number"
                              value={row.amount}
                              onChange={(e) => updateUserRow(index, "amount", parseFloat(e.target.value) || 0)}
                              className="border-white/10 bg-black/40 text-white"
                              placeholder="Enter number of employees"
                            />
                          </div>
                        </div>
                        <Button
                          onClick={() => removeUserRow(index)}
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documentation Section 1 */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Documentation</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Documentation"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button
                onClick={saveResponses}
                disabled={saveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
              {saveStatus === 'saving' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Save Documentation
                </>
              )}
            </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Save Status Messages */}
          {saveStatus === 'saved' && (
            <div className="rounded-md bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-300">
              Documentation saved successfully to SharePoint
            </div>
          )}
          {saveStatus === 'error' && saveError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-300">
              Error saving documentation: {saveError}
            </div>
          )}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white text-base font-medium">
                i. Document our conclusion on the performance of the following procedures with respect to the management's expert:
              </Label>
              <ul className="list-disc list-inside space-y-1 ml-4 text-gray-300 text-sm">
                <li>Evaluate the competence, capabilities and objectivity of that expert;</li>
                <li>Obtain an understanding of the work of that expert; and</li>
                <li>Evaluate the appropriateness of that expert's work as audit evidence for the relevant assertion.</li>
              </ul>
              <div className="mt-3">
                <DocumentManager
                  sectionKey="payroll"
                  itemId="Actuary-1"
                  itemQuestion="Document our conclusion on the performance of the following procedures with respect to the management's expert"
                  itemTextResponse={questionResponses['Actuary-1']?.textResponse}
                  itemDocuments={(questionResponses['Actuary-1']?.documents as DocumentItem[]) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange('Actuary-1', docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange('Actuary-1', textResp)}
                />
              </div>
            </div>

            <div className="space-y-2 pt-4 border-t border-white/10">
              <Label className="text-white text-base font-medium">
                ii. Document the actuary reports received for the current period and our testing on the same.
              </Label>
              <div className="mt-3">
                <DocumentManager
                  sectionKey="payroll"
                  itemId="Actuary-2"
                  itemQuestion="Document the actuary reports received for the current period and our testing on the same"
                  itemTextResponse={questionResponses['Actuary-2']?.textResponse}
                  itemDocuments={(questionResponses['Actuary-2']?.documents as DocumentItem[]) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange('Actuary-2', docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange('Actuary-2', textResp)}
                />
              </div>
            </div>
            
            {/* Inference and Conclusion Question */}
            <div className="space-y-2 pt-4 border-t border-white/10">
              <Label className="text-white text-base font-medium">
                Document the inference and conclusion from the above procedure performed.
              </Label>
              <div className="mt-3">
                <DocumentManager
                  sectionKey="payroll"
                  itemId="ACTUARY-INFERENCE"
                  itemQuestion="Document the inference and conclusion from the above procedure performed."
                  itemTextResponse={inferenceQuestion.textResponse}
                  itemDocuments={(inferenceQuestion.documents as DocumentItem[]) || []}
                  onDocumentsChange={(docs) => setInferenceQuestion({ ...inferenceQuestion, documents: docs })}
                  onTextResponseChange={(textResp) => setInferenceQuestion({ ...inferenceQuestion, textResponse: textResp })}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Mapping Table")]: "Mapping Table",
              [getSectionQuestionId("Run Actuary Testing")]: "Run Actuary Testing",
              [getSectionQuestionId("Actuary Testing Summary")]: "Actuary Testing Summary",
              [getSectionQuestionId("Documentation")]: "Documentation",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
